import {
  ColumnDataType,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import { isExpressionValue } from '../utils'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class CustomSQL extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.customsql',
        group: 'Custom',
        title: 'Custom SQL Select',
        description: `
      This component executes an arbitrary SELECT SQL statement.\n

      Use $a, $b and $c as placeholders for the different inputs.\n
      `,
        starred: true
      },
      [
        {
          name: 'sourcea',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table a',
          optional: true
        },
        {
          name: 'sourceb',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table b',
          optional: true
        },
        {
          name: 'sourcec',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table c',
          optional: true
        },
        {
          name: 'sql',
          type: WorkflowComponentParamType.StringSql,
          title: 'SQL SELECT statement',
          mode: WorkflowComponentParameterMode.Multiline,
          placeholder:
            'SELECT ST_Centroid(geom) AS geom,\n  AVG(value) AS average_value,\n  category\nFROM $a\nGROUP BY category',
          allowExpressions: false
        }
      ]
    )
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    if (params.sql && !isExpressionValue(params.sql as WorkflowComponentValue)) {
      const missingTables: string[] = []
      const value = (params.sql as WorkflowComponentValue).value as string
      for (const source of ['sourcea', 'sourceb', 'sourcec']) {
        if (value && value.includes(`$${source[source.length - 1]}`) && !resolvedParamValues[source]) {
          missingTables.push(source)
        }
      }
      if (missingTables.length > 0) {
        problems.push({
          parameter: 'sql',
          type: WorkflowParameterValueErrorType.WrongValue,
          message: `Table${missingTables.length > 1 ? 's' : ''} ${missingTables
            .map((t) => `"${t[t.length - 1]}"`)
            .join(', ')} used in statement but not provided`
        })
      }
    }
    return problems
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    let select = (params.sql as string) ?? ''
    if (connection.type === Provider.DatabricksRest) {
      select = select
        .replace(
          /`\$a`/g,
          (params.sourcea as string)
            ?.split('.')
            .map((x: string) => `\`${x}\``)
            .join('.')
        )
        .replace(
          /`\$b`/g,
          (params.sourceb as string)
            ?.split('.')
            .map((x: string) => `\`${x}\``)
            .join('.')
        )
        .replace(
          /`\$c`/g,
          (params.sourcec as string)
            ?.split('.')
            .map((x: string) => `\`${x}\``)
            .join('.')
        )
    } else if (connection.type === Provider.Oracle) {
      // the quoteName method here will just remove admin.admin.table -> admin.table
      // and perform clean up of the table name
      // We are assuming that in oracle the user will never quote the table name,
      // so that case is not considered here
      select = select
        .replace(/\$a/g, connection.quoteName(params.sourcea as string))
        .replace(/\$b/g, connection.quoteName(params.sourceb as string))
        .replace(/\$c/g, connection.quoteName(params.sourcec as string))
    }
    select = select
      .replace(/\$a/g, params.sourcea as string)
      .replace(/\$b/g, params.sourceb as string)
      .replace(/\$c/g, params.sourcec as string)
      .replace(/\$dryrun/g, connection.type === Provider.Oracle ? (dryRun ? '1' : '0') : dryRun.toString())
      .replace(/(--.*?$)|(;+\s*)$/gm, '') // Removes trailing comments and semicolons

    const query = connection.getCreateTempTableQuery(tablename, select, codeType, null)

    return {
      query,
      outputs: { result: tablename }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '2.0.0'
  }

  parseExpectedSchema(expectedSchema: string): TableSchema {
    return expectedSchema.split(',').map((column) => {
      const [name, type] = column.split(':')
      return { name, type: type as ColumnDataType }
    })
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }
}

export { CustomSQL }
